import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import {
    WorkspaceAvatarGroup,
    type WorkspaceAvatarGroupItem,
} from "@components/avatar/WorkspaceAvatarGroup";
import { pluralizeWithCount } from "@common/helpers/string";
import { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    ServiceTemplateScheduleCallbacks,
    ServiceTemplateScheduleTableOptions,
} from "../ServiceTemplateSchedulesList";
import styles from "./ServiceTemplateScheduleClientCell.module.scss";

export const ServiceTemplateScheduleClientCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ServiceTemplateScheduleRow, string>) => {
        const meta = useViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >(table);
        const workspaceInfoByUuid = meta.options?.workspaceInfoByUuid;

        // Group rows (schedules sharing a groupId) can span multiple clients —
        // that's the whole reason a schedule gets grouped in the first place —
        // so show every distinct client among the member schedules as a
        // preview; the individual client is shown on the expanded leaf rows.
        const { groupWorkspaces, distinctClientCount } = useMemo(() => {
            if (row.original.rowType !== "group") {
                return { groupWorkspaces: [], distinctClientCount: 0 };
            }
            const seen = new Set<string>();
            const workspaces: WorkspaceAvatarGroupItem[] = [];
            for (const child of row.original.children) {
                if (!child.workspaceId || seen.has(child.workspaceId)) {
                    continue;
                }
                // Count every distinct workspace the group spans, even ones we
                // can't resolve to an icon yet, so the label never undercounts
                // the real number of clients.
                seen.add(child.workspaceId);
                const info = workspaceInfoByUuid?.get(child.workspaceId);
                if (info) {
                    workspaces.push({ workspace: info });
                }
            }
            return {
                groupWorkspaces: workspaces,
                distinctClientCount: seen.size,
            };
        }, [row.original, workspaceInfoByUuid]);

        if (row.original.rowType === "group") {
            return (
                <ViewTableCell>
                    <WorkspaceAvatarGroup
                        workspaces={groupWorkspaces}
                        max={3}
                        size="xs"
                        label={
                            <span className="text-secondary">
                                {pluralizeWithCount(
                                    "client",
                                    distinctClientCount,
                                )}
                            </span>
                        }
                    />
                </ViewTableCell>
            );
        }

        const { workspaceId } = row.original;
        const workspaceInfo = workspaceId
            ? workspaceInfoByUuid?.get(workspaceId)
            : undefined;
        const clientName = workspaceInfo?.name ?? "-";

        return (
            <ViewTableCell>
                <div className={styles.container}>
                    {workspaceInfo && (
                        <WorkspaceAvatar
                            workspace={workspaceInfo}
                            size="xs"
                            aria-hidden
                        />
                    )}
                    <span className="text-secondary">{clientName}</span>
                </div>
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleClientCell.displayName =
    "ServiceTemplateScheduleClientCell";
